import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import HeroCarousel from "../components/HeroCarousel";
import ProductCard from "../components/ProductCard";
import { Truck, ShieldCheck, Sparkles, Heart, Gift } from "lucide-react";

const Home = () => {
  const { t, lang, settings } = useApp();
  const [products, setProducts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/banners").then((r) => setBanners(r.data));
    api.get("/products?featured=true").then((r) => setProducts(r.data));
    api.get("/categories").then((r) => setCategories((r.data || []).filter((c) => c.active && !c.parent_slug).sort((a, b) => a.order - b.order)));
  }, []);

  return (
    <div>
      <HeroCarousel banners={banners} />

      {/* Quick Category Strip */}
      {categories.length > 0 && (
        <section className="bg-ivory py-8 sm:py-10" data-testid="category-strip">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <div className="text-center mb-6">
              <div className="text-[11px] tracking-[0.3em] uppercase text-holy-gold-dark font-semibold">
                ✦ {lang === "id" ? "Belanja per Kategori" : "Shop by Category"} ✦
              </div>
            </div>
            <div className="flex gap-4 sm:gap-6 overflow-x-auto sm:overflow-visible sm:grid sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 pb-2 sm:pb-0 -mx-2 px-2 snap-x">
              {categories.map((c) => (
                <Link
                  key={c.id}
                  to={`/shop?cat=${c.slug}`}
                  data-testid={`category-tile-${c.slug}`}
                  className="snap-start flex-shrink-0 group flex flex-col items-center text-center w-[100px] sm:w-auto"
                >
                  <div className="w-[72px] h-[72px] sm:w-20 sm:h-20 rounded-full bg-cream border border-border/60 group-hover:border-holy-gold group-hover:shadow-card flex items-center justify-center text-3xl sm:text-4xl transition-all duration-300 group-hover:-translate-y-1 overflow-hidden">
                    {c.image_url ? (
                      <img src={c.image_url} alt={lang === "id" ? c.name_id : c.name_en} className="w-full h-full object-cover" />
                    ) : (
                      <span aria-hidden="true">{c.icon || "✦"}</span>
                    )}
                  </div>
                  <div className="mt-3 font-serif text-sm sm:text-base text-foreground group-hover:text-holy-red transition-colors">
                    {lang === "id" ? c.name_id : c.name_en}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Trust badges */}
      <section className="bg-cream py-8 border-y border-border/60">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Heart, t: lang === "id" ? "100% Handmade" : "100% Handmade", s: lang === "id" ? "Dibuat satu per satu" : "Crafted one by one" },
            { icon: Truck, t: lang === "id" ? "Pengiriman Cepat" : "Fast Shipping", s: "JNE, J&T, Gojek, Grab" },
            { icon: ShieldCheck, t: lang === "id" ? "Pembayaran Aman" : "Secure Payment", s: "QRIS, VA, Credit Card" },
            { icon: Gift, t: lang === "id" ? "Cocok untuk Hadiah" : "Gift Ready", s: lang === "id" ? "Kemasan rapi" : "Beautiful packaging" },
          ].map((f, idx) => (
            <div key={idx} className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-ivory border border-border/60 flex items-center justify-center text-holy-red shrink-0">
                <f.icon className="w-4 h-4" />
              </div>
              <div>
                <div className="font-serif text-base text-foreground">{f.t}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{f.s}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Products */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="text-center mb-12">
          <div className="text-[11px] tracking-[0.3em] uppercase text-holy-gold-dark font-semibold mb-3">
            ✦ {t("featured")} ✦
          </div>
          <h2 className="font-serif text-3xl sm:text-5xl text-foreground mb-3">
            {lang === "id" ? "Pilihan Terbaik dari Studio Kami" : "Curated From Our Studio"}
          </h2>
          <p className="font-script text-2xl text-holy-red">handmade with love</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="featured-grid">
          {products.map((p, idx) => (
            <ProductCard key={p.id} product={p} index={idx} />
          ))}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 border border-holy-red text-holy-red hover:bg-holy-red hover:text-ivory rounded-full px-8 py-3 font-sans transition-all duration-300"
            data-testid="see-all-products"
          >
            {t("nav_shop")} →
          </Link>
        </div>
      </section>

      {/* Story Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-holy-gold-dark font-semibold mb-3">
              ✦ {lang === "id" ? "Cerita Kami" : "Our Story"} ✦
            </div>
            <h2 className="font-serif text-3xl sm:text-5xl text-foreground leading-tight mb-6">
              {lang === "id"
                ? "Setiap tangkai dirakit dengan tangan & sepenuh hati"
                : "Every stem assembled by hand & whole heart"}
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4 whitespace-pre-line">
              {lang === "id" ? settings?.about_id : settings?.about_en}
            </p>
            <p className="font-script text-3xl text-holy-red mb-6">{t("handmade_with_love")}</p>
            <Link to="/about" className="inline-flex items-center text-holy-red font-medium hover:underline">
              {lang === "id" ? "Baca lebih lanjut" : "Read more"} →
            </Link>
          </div>
          <div className="relative">
            {banners[1] && (
              <img src={banners[1].image_url} alt="Story" className="rounded-2xl object-cover w-full h-[500px] shadow-card" />
            )}
            <div className="absolute -bottom-6 -left-6 bg-ivory rounded-2xl px-6 py-4 shadow-card border border-border/60">
              <div className="font-script text-3xl text-holy-red leading-none">100%</div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Handmade</div>
            </div>
          </div>
        </div>
      </section>

      {/* Payment & Shipping showcase */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-cream rounded-2xl p-8">
            <div className="text-[11px] tracking-[0.3em] uppercase text-holy-gold-dark font-semibold mb-2">{t("payment_methods")}</div>
            <h3 className="font-serif text-2xl mb-5">QRIS · Transfer · Kartu Kredit · E-Wallet</h3>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {["QRIS", "BCA", "Mandiri", "BNI", "Visa", "Mastercard", "GoPay", "OVO", "Dana", "ShopeePay"].map((m) => (
                <div key={m} className="bg-ivory border border-border/50 rounded-lg py-2 px-3 text-center text-xs font-medium">{m}</div>
              ))}
            </div>
          </div>
          <div className="bg-cream rounded-2xl p-8">
            <div className="text-[11px] tracking-[0.3em] uppercase text-holy-gold-dark font-semibold mb-2">{t("shipping_methods")}</div>
            <h3 className="font-serif text-2xl mb-5">JNE · J&T · Pos · Gojek · Grab</h3>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {["JNE", "J&T Express", "Pos Indonesia", "SiCepat", "Gojek", "Grab Express", "DHL", "FedEx"].map((m) => (
                <div key={m} className="bg-ivory border border-border/50 rounded-lg py-2 px-3 text-center text-xs font-medium">{m}</div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
